import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import HowToSteps, { HowToStep } from '@comp/learn/how-to-steps'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  stepsEyebrow: string
  stepsTitle: string
  stepsBody: string
  docLabel: string
  steps: HowToStep[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Secure vendor remote access for OT networks, without inbound VPN',
    metaDescription:
      'How to give machine builders and maintenance vendors access to OT systems: brokered sessions from the DMZ, time-boxed accounts, recording, and approval gates. Fits the Purdue model, runs air-gapped.',
    heroEyebrow: 'How-to · OT vendor access',
    heroTitle: ['The vendor reaches the PLC.', 'Never the network.'],
    heroBody:
      'Every plant depends on outsiders: the machine builder who tunes the line, the integrator who patches SCADA, the OEM on a support contract. The usual answer is a VPN into the control network, which is also the usual finding in incident reports. The alternative: a brokered, recorded, time-boxed session that terminates in the DMZ, so the vendor sees one system, not a network.',
    badges: ['Purdue-model friendly', 'No inbound VPN', 'Runs air-gapped'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'See the VPAM feature',
    stepsEyebrow: 'Walkthrough',
    stepsTitle: 'From VPN exception to governed vendor session.',
    stepsBody:
      'Five steps that fit the reference architecture you already run: enterprise zone, industrial DMZ, control zone.',
    docLabel: 'Explore vendor privileged access',
    steps: [
      {
        title: 'Place the gateway in the industrial DMZ',
        body: 'Monopam deploys self-hosted, next to the systems it protects. The gateway sits in the DMZ between the enterprise and control zones (level 3.5 in Purdue terms) and is the only path a vendor session takes. No inbound tunnel crosses into the control network, and nothing about the deployment requires an outbound SaaS dependency.',
        hint: 'OT-focused remote-access SaaS tools relay vendor sessions through a cloud the plant does not control. A self-hosted broker keeps the session, the recording, and the credentials on site, which is what air-gapped and regulated sites actually need.',
      },
      {
        title: 'Model each vendor as an organization',
        body: 'Create a vendor organization per supplier: authorized email domains so invitations cannot drift to personal mailboxes, IP restrictions for where their engineers may connect from, a contract window, and a default access duration for every account under it.',
      },
      {
        title: 'Invite engineers through the approval flow',
        body: 'A vendor engineer is invited by name, with an expiry and an MFA requirement. The invitation itself passes an approval before it is sent; enrollment is a passwordless magic link. No shared "maintenance" account, no password exchanged over email.',
        hint: 'The expiry is enforced in the PAM layer and mirrored to the identity layer. When the contract ends, both doors close on their own.',
      },
      {
        title: 'Gate and record every connection',
        body: 'Point the vendor at the one resource they maintain: the engineering workstation, the HMI, the jump host in front of the PLC subnet. Their connection can require just-in-time approval from your team before it starts, is always recorded, and notifies the sponsor the moment it opens.',
        hint: 'Recording covers the full session with keystroke transcription, which is the evidence NIS2 supply-chain measures and IEC 62443 remote-access requirements keep asking for.',
      },
      {
        title: 'Let expiry and reviews do the cleanup',
        body: 'Accounts disable themselves at expiry or after inactivity. Periodic access reviews put the remaining list in front of an approver; a rejection disables the account. The audit trail per vendor answers the who-what-when for every session of the engagement.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      {
        label: 'Vendor Privileged Access',
        description: 'The Monopam feature this guide builds on.',
        href: '/pam/vendor-access',
      },
      {
        label: 'Session recording',
        description: 'Video and keystroke evidence for every vendor session.',
        href: '/pam/session-recording',
      },
      {
        label: 'IEC 62443 compliance',
        description: 'SRs to enforced controls, mapped for OT assessments.',
        href: '/compliance/iec-62443',
      },
    ],
  },
  tr: {
    metaTitle: 'OT ağları için gelen VPN olmadan güvenli tedarikçi uzaktan erişimi',
    metaDescription:
      'Makine üreticilerine ve bakım tedarikçilerine OT sistemlerine erişim nasıl verilir: DMZ üzerinden aracılı oturumlar, süreli hesaplar, kayıt ve onay kapıları. Purdue modeline uyar, kapalı ağda çalışır.',
    heroEyebrow: 'Rehber · OT tedarikçi erişimi',
    heroTitle: ["Tedarikçi PLC'ye ulaşır.", 'Ağa asla.'],
    heroBody:
      'Her tesis dışarıdan birilerine bağımlıdır: hattı ayarlayan makine üreticisi, SCADA yamasını geçen entegratör, destek sözleşmeli OEM. Alışılmış cevap kontrol ağına açılan bir VPN, ki olay raporlarındaki alışılmış bulgu da odur. Alternatif: DMZ içinde sonlanan aracılı, kayıtlı ve süreli bir oturum. Tedarikçi ağı değil, tek bir sistemi görür.',
    badges: ['Purdue modeliyle uyumlu', 'Gelen VPN yok', 'Kapalı ağda çalışır'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: "VPAM özelliğine bakın",
    stepsEyebrow: 'Adım adım',
    stepsTitle: 'VPN istisnasından yönetilen tedarikçi oturumuna.',
    stepsBody:
      'Zaten işlettiğiniz referans mimariye uyan beş adım: kurumsal bölge, endüstriyel DMZ, kontrol bölgesi.',
    docLabel: 'Tedarikçi ayrıcalıklı erişimini keşfedin',
    steps: [
      {
        title: "Gateway'i endüstriyel DMZ'ye yerleştirin",
        body: "Monopam kendi altyapınızda, koruduğu sistemlerin yanında kurulur. Gateway, kurumsal ve kontrol bölgeleri arasındaki DMZ'de (Purdue diliyle seviye 3.5) durur ve tedarikçi oturumunun izlediği tek yoldur. Kontrol ağına giren bir tünel yoktur; kurulumun dışarıya SaaS bağımlılığı da yoktur.",
        hint: "OT odaklı uzaktan erişim SaaS araçları tedarikçi oturumunu tesisin kontrol etmediği bir buluttan geçirir. Kendi altyapınızdaki aracı; oturumu, kaydı ve kimlik bilgilerini sahada tutar. Kapalı ağlı ve regülasyona tabi tesislerin gerçek ihtiyacı budur.",
      },
      {
        title: 'Her tedarikçiyi bir kuruluş olarak modelleyin',
        body: 'Tedarikçi başına bir kuruluş oluşturun: davetler kişisel posta kutularına kaymasın diye yetkili e-posta alan adları, mühendislerinin nereden bağlanabileceği için IP kısıtları, bir sözleşme penceresi ve altındaki her hesap için varsayılan erişim süresi.',
      },
      {
        title: 'Mühendisleri onay akışıyla davet edin',
        body: 'Tedarikçi mühendisi ismen, son kullanma tarihi ve MFA gereksinimiyle davet edilir. Davetin kendisi gönderilmeden önce onaydan geçer; kayıt parolasız magic link ile yapılır. Ortak "bakım" hesabı yok, e-postayla paylaşılan parola yok.',
        hint: 'Süre PAM katmanında uygulanır ve kimlik katmanına aynalanır. Sözleşme bittiğinde iki kapı da kendiliğinden kapanır.',
      },
      {
        title: 'Her bağlantıyı kapılayın ve kaydedin',
        body: "Tedarikçiyi bakımını yaptığı tek kaynağa yönlendirin: mühendislik istasyonu, HMI ya da PLC alt ağının önündeki jump host. Bağlantısı başlamadan önce ekibinizden tam zamanında onay isteyebilir, her zaman kaydedilir ve açıldığı anda sponsoru bilgilendirir.",
        hint: "Kayıt, tuş vuruşu dökümüyle oturumun tamamını kapsar. NIS2 tedarik zinciri önlemlerinin ve IEC 62443 uzaktan erişim gereksinimlerinin sorup durduğu kanıt tam olarak budur.",
      },
      {
        title: 'Temizliği süre dolumu ve gözden geçirmelere bırakın',
        body: 'Hesaplar süre dolduğunda ya da hareketsizlikte kendini devre dışı bırakır. Periyodik erişim gözden geçirmeleri kalan listeyi bir onaylayıcının önüne koyar; ret hesabı kapatır. Tedarikçi bazındaki denetim izi, çalışmanın her oturumu için kim-ne-ne zaman sorusunu yanıtlar.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      {
        label: 'Tedarikçi Ayrıcalıklı Erişimi',
        description: 'Bu rehberin üzerine kurulduğu Monopam özelliği.',
        href: '/pam/vendor-access',
      },
      {
        label: 'Oturum kaydı',
        description: 'Her tedarikçi oturumu için video ve tuş vuruşu kanıtı.',
        href: '/pam/session-recording',
      },
      {
        label: 'IEC 62443 uyumu',
        description: "OT denetimleri için SR'lerin uygulanmış kontrollere eşlenmesi.",
        href: '/compliance/iec-62443',
      },
    ],
  },
}

type Props = { locale: string }

export default function OtVendorAccessGuidePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/learn/ot-vendor-access"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          badges={c.badges}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/pam/vendor-access' }}
        />
        <HowToSteps
          eyebrow={c.stepsEyebrow}
          title={<>{c.stepsTitle}</>}
          body={c.stepsBody}
          steps={c.steps}
          schemaName={c.metaTitle}
          docHref="/pam/vendor-access"
          docLabel={c.docLabel}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
